import React, { useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fileDownloader, timeFormatter } from 'utils/helper/helper';
import RenderTable from 'components/molecule/table/RenderTable';
import TableCell from 'components/atoms/tableCell/TableCell';
import PageTitle from 'components/atoms/pageTitle/PageTitle';
import TableRow from 'components/atoms/TableRow/TableRow';
import IntlBinsFilterForm from './IntlBinsFilterForm/IntlBinsFilterForm';
import FileInputButton from 'components/atoms/FileInput/FileInputButton';
import { handleAPI } from 'utils/api/api';
import { showToastr } from 'redux/slices/toastrSlice';
import { deleteCardBin, getCardBins } from 'redux/slices/payment/cardBinsSlice';
import Button from 'components/atoms/button/Button';
import PlusIcon from 'components/icons/PlusIcon';
import AddCardBinModal from './AddCardBinModal/AddCardBinModal';
import TableActionButtons from 'components/molecule/TableActionButtons/TableActionButtons';
import { openActionModal } from 'redux/slices/confirmationModalSlice';
import { useDispatchParams, withParamsState } from 'hooks/searchParams';
import { filters, timeColumn, defaultSort } from './queryFields';
import { FilterBarForm } from 'components/molecule/filterForms';
import { filterFields } from './IntlBinsFilterForm/filterFields';

const CardBins = ({ prepaid = false, state: { pagination, setPagination } }) => {
  const dispatch = useDispatch();
  const { cardBins, totalPage, totalCount, isLoading } = useSelector((state) => state.cardBins);
  const [selectedBin, setSelectedBin] = useState(undefined);

  const loadAction = useCallback(
    (queryParams) =>
      getCardBins({
        ...queryParams,
        ...(prepaid ? { 'filter[card_sub_type_eq]': 'PREPAID' } : {}),
      }),
    [prepaid]
  );

  useDispatchParams(loadAction);

  const handleDeleteBin = (id) => {
    dispatch(
      openActionModal({
        heading: `Are you sure to delete the BIN?`,
        action: () => dispatch(deleteCardBin(id)),
      })
    );
  };

  const uploadFile = async (file) => {
    if (file) {
      const formData = new FormData();
      formData.append('file', file);
      const response = await handleAPI('platform/bin_cards/csv_import', 'post', '', formData);
      if (response) {
        fileDownloader(response?.data?.log_file, 'intl_bins.csv');
        if (response.status === 202) {
          dispatch(
            showToastr({
              message: 'Import aborted. Check downloaded Log file for details.',
              visible: true,
              type: 'error',
            })
          );
        }
      }
    }
  };

  return (
    <div>
      <AddCardBinModal
        id={selectedBin}
        setId={setSelectedBin}
        prepaid={prepaid}
      />
      <div className='mt-[16px] flex flex-wrap items-center justify-end gap-x-3 gap-y-6'>
        <PageTitle
          title={prepaid ? 'Prepaid Bins' : 'International BINs'}
          className={'mr-auto'}
        />
        <FileInputButton
          title='Import CSV'
          id='file-upload-input'
          accept='.csv'
          handleFile={uploadFile}
          className='mr-5'
        />
        <FilterBarForm FilterForm={IntlBinsFilterForm} />
      </div>
      <div className={'mt-6 flex justify-between'}>
        <Button
          title={'Add BIN'}
          icon={<PlusIcon />}
          className={'ml-auto'}
          onClickHandler={() => window.addCardBinModal.showModal()}
        />
      </div>

      <RenderTable
        dataCount={cardBins?.length}
        columns={[
          'Bin',
          'Card Brand',
          'Card Type',
          'Card Subtype',
          'Issuing organisation',
          'Country',
          'Country ISO 2',
          'Country ISO 3',
          'Country ISO numeric',
          'Created At',
          'Updated At',
          'Actions',
        ]}
        totalPages={totalPage}
        totalCount={totalCount}
        pagination={pagination}
        compactPlaceholder
        setPagination={setPagination}
        isLoading={isLoading}>
        {cardBins?.map((cardBin) => (
          <TableRow key={cardBin?.id}>
            <TableCell>{cardBin?.bin}</TableCell>
            <TableCell>{cardBin?.card_brand}</TableCell>
            <TableCell>{cardBin?.card_type}</TableCell>
            <TableCell>{cardBin?.card_sub_type}</TableCell>
            <TableCell>{cardBin?.issuing_organisation}</TableCell>
            <TableCell>{cardBin?.country}</TableCell>
            <TableCell>{cardBin?.iso2}</TableCell>
            <TableCell>{cardBin?.iso3}</TableCell>
            <TableCell>{cardBin?.iso_numeric}</TableCell>
            <TableCell>{timeFormatter(cardBin?.created_at)}</TableCell>
            <TableCell>{timeFormatter(cardBin?.updated_at)}</TableCell>
            <TableActionButtons
              rowTitle={'entry'}
              onDeleteClick={() => handleDeleteBin(cardBin?.id)}
              onEditClick={() => {
                setSelectedBin(cardBin?.id);
                window.addCardBinModal.showModal();
              }}
              editPrivilege={'payments.bin_routing.write'}
              deletePrivilege={'payments.bin_routing.destroy'}
            />
          </TableRow>
        ))}
      </RenderTable>
    </div>
  );
};

export default withParamsState(CardBins, { filterFields, filters, timeColumn, defaultSort });
